import { defineMessages, useIntl } from 'react-intl';

import Suspense from '@atlaskit/react-ufo/suspense';

import { LoadingSpinner } from '@/components/global-nav/user-profile-new/src/components/spinner';
import {
	PriceFinderMessages,
	ProductPlan,
} from '@/components/wpl/pricing/src/price-finder/constants';
import PriceFinderSection from '@/components/wpl/pricing/src/price-finder/price-finder';
import { PriceFinderContextProvider } from '@/components/wpl/pricing/src/price-finder/price-finder-context.tsx';
import { createPlanFeatureMessages } from '@/components/wpl/pricing/src/price-finder/utils/create-price-finder-features.tsx';
import { usePricingPlanComparison } from '@/components/wpl/pricing/src/pricing-content-context.tsx';
import { AppPricingData } from '@/contentful/product-catalog';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';
import { UserLocale } from '@/utils/useGeoCurrency.ts';

export type ConfluencePriceFinderProps = {
	pricing?: AppPricingData;
	countryCode?: UserLocale;
};

const ConfluencePriceFinder = (props: ConfluencePriceFinderProps) => {
	const content = {
		free: usePricingPlanComparison('Free'),
		standard: usePricingPlanComparison('Standard'),
		premium: usePricingPlanComparison('Premium'),
		enterprise: usePricingPlanComparison('Enterprise'),
	};

	const freePlanFeatureMessages = createPlanFeatureMessages(content, 'free');
	const standardPlanFeatureMessages = createPlanFeatureMessages(content, 'standard');
	const premiumPlanFeatureMessages = createPlanFeatureMessages(content, 'premium');
	const enterprisePlanFeatureMessages = createPlanFeatureMessages(content, 'enterprise');

	const intl = useIntl();

	const signupLimit = checkFeatureFlagGate(featureFlags.gwp11139Confluence250kPricingUpdate)
		? 250000
		: 150000;

	// planNames should not be translated
	const confluenceProductPlans: ProductPlan[] = [
		{
			planName: 'Free',
			offeringSku: 'confluence-free',
			description: intl.formatMessage({
				id: 'ConfluencePricingPageTeam24.priceFinder.freeDescription',
				defaultMessage: 'Free forever for 10 users',
			}),
			recommendationLimit: 0,
			signupLimit,
			signupURLSuffix: '?bundle=confluence&edition=free&signupSource=pricing&editionIntent=free',
			planFeatureMessages: freePlanFeatureMessages,
		},
		{
			planName: 'Standard',
			offeringSku: 'confluence-standard',
			description: intl.formatMessage({
				id: 'ConfluencePricingPageTeam24.priceFinder.standardDescription',
				defaultMessage: 'Everything you need to get started',
			}),
			recommendationLimit: 10,
			signupLimit,
			signupURLSuffix: '?bundle=confluence&edition=standard&signupSource=pricing',
			planFeatureMessages: standardPlanFeatureMessages,
		},
		{
			planName: 'Premium',
			offeringSku: 'confluence-premium',
			description: intl.formatMessage({
				id: 'ConfluencePricingPageTeam24.priceFinder.premiumDescription',
				defaultMessage: 'Connect work across teams',
			}),
			recommendationLimit: 250,
			signupLimit,
			signupURLSuffix: '?bundle=confluence&edition=premium&signupSource=pricing',
			planFeatureMessages: premiumPlanFeatureMessages,
		},
		{
			planName: 'Enterprise',
			offeringSku: 'confluence-enterprise-sku',
			description: intl.formatMessage({
				id: 'ConfluencePricingPageTeam24.priceFinder.enterpriseDescription',
				defaultMessage: 'Advanced analytics, scale and security for enterprises',
			}),
			recommendationLimit: 5000,
			signupLimit,
			planFeatureMessages: enterprisePlanFeatureMessages,
		},
	];

	const messages: PriceFinderMessages = defineMessages({
		headerText: {
			id: 'ConfluencePricingPageTeam24.priceFinder.headerText',
			defaultMessage: 'Build Your <blue>Source of Truth</blue>',
		},
		unitCountInputTextRedesign: {
			id: 'ConfluencePricingPage.priceFinder.unitCountInputTextRedesign',
			defaultMessage: 'Team size:',
		},
		unitCountInputDirectBuy: {
			id: 'ConfluencePricingPage.priceFinder.unitCountInputDirectBuy',
			defaultMessage: "What's your team size?",
		},
		unitCountInputTerm: {
			id: 'ConfluencePricingPage.priceFinder.unitCountInputTerm',
			defaultMessage: 'users',
		},
		billingCycleText: {
			id: 'ConfluencePricingPage.priceFinder.billingCycleText',
			defaultMessage: 'Billing cycle:',
		},
		unitCountInputText: {
			id: 'ConfluencePricingPage.priceFinder.unitCountInputText',
			defaultMessage: 'How many users are on your team?',
		},
		unitCountInputTooltip: {
			id: 'ConfluencePricingPage.priceFinder.unitCountInputTooltip',
			defaultMessage:
				'A Confluence user is any user who can log in to Confluence, and who exists in User Management',
		},
		monthlyExampleHelpText: {
			id: 'ConfluencePricingPage.priceFinder.monthlyExampleHelpText',
			defaultMessage:
				'Monthly plans give you flexibility as your number of active users changes, so pay for seats as you use them. Remember that the number of seats may change the total price.',
		},
		annualExampleHelpText: {
			id: 'ConfluencePricingPage.priceFinder.annualExampleHelpText',
			defaultMessage:
				'Annual plans give you flexibility and help you prepare for growth. Buy a group of seats at a flat rate now, then fill your empty seats through the year at no extra cost.',
		},
		exampleStandardPlan: {
			id: 'ConfluencePricingPage.priceFinder.exampleStandardPlan',
			defaultMessage: 'Pricing for {unitCount} users on Standard plan',
			description: 'Standard is a plan name and should not be translated',
		},
		cardOverSignupLimit: {
			id: 'ConfluencePricingPage.priceFinder.cardOverSignupLimit',
			defaultMessage: "Your user count exceeds this plan's limit",
		},
		cardEnterpriseMinimumCount: {
			id: 'ConfluencePricingPage.priceFinder.cardEnterpriseMinimumCount',
			defaultMessage:
				'Enterprise plan pricing is available when you enter {enterpriseMinimumCount} or more users above',
			description: 'Enterprise is a plan name and should not be translated',
		},
		cardUnitCountTierRange: {
			id: 'ConfluencePricingPage.priceFinder.cardUnitCountTierRange',
			defaultMessage: 'User tier: {lowerLimit} - {upperLimit}',
		},
		cardMonthlyPerUnitCount: {
			id: 'ConfluencePricingPageTeam24.priceFinder.cardMonthlyPerUnitCount',
			defaultMessage: 'per user / month',
		},
		cardFreeDescription: {
			id: 'ConfluencePricingPage.priceFinder.cardFreeDescription',
			defaultMessage: 'Always free for 10 users',
		},
	});

	const sourceFromProductPlans = checkFeatureFlagGate(
		featureFlags.gwp7546ConfluencePricingCtaCtaUrlSource,
	);

	return (
		<Suspense interactionName={'conf-price-finder'} fallback={LoadingSpinner}>
			<PriceFinderContextProvider
				dataProductKey="confluence"
				ctaUrlSource={sourceFromProductPlans ? 'productPlans' : undefined}
				productPlans={confluenceProductPlans}
				defaultUnitCount={300}
			>
				<PriceFinderSection
					messages={messages}
					serverSideCCPPricing={props.pricing?.ccpPricingData?.confluence}
					countryCode={props.countryCode}
				/>
			</PriceFinderContextProvider>
		</Suspense>
	);
};

ConfluencePriceFinder.displayName = 'ConfluencePriceFinderTeam24';

export default ConfluencePriceFinder;
